import { useId } from "react";
import { Controller, useFormContext, type FieldPath, type FieldValues } from "react-hook-form";

import { Label } from "../../atoms/label";
import { TextArea } from "../../atoms/text-area";
import type { TextAreaProps } from "../../atoms/text-area";
import { Typography } from "../../atoms/typography";
import { cn, platformProps } from "../../lib";
import { Box } from "../../primitives";
import { FormFieldMessage } from "./form-field-message";
import type { FormFieldBaseProps } from "./types";

export type FormTextAreaProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = FormFieldBaseProps<TFieldValues, TName> &
  Omit<TextAreaProps, "state" | "value" | "onChangeText" | "onBlur"> & {
    /**
     * Shows "count / maxLength" at the right of the label row; needs `maxLength`.
     * Without a `label` the row holds only the count.
     */
    showCharCount?: boolean;
    maxLength?: number;
  };

export const FormTextArea = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  name,
  label,
  required = false,
  helperText,
  hideErrorMessage = false,
  errorMessage,
  containerClassName,
  testID,
  showCharCount = false,
  maxLength,
  id: idProp,
  ...textAreaProps
}: FormTextAreaProps<TFieldValues, TName>) => {
  const generatedId = useId();
  const id = idProp ?? generatedId;
  const { control } = useFormContext<TFieldValues>();

  return (
    <Box
      {...platformProps({ slot: "form-text-area", testID })}
      className={cn("gap-spacing-xxs flex w-full flex-col", containerClassName)}
    >
      <Controller
        name={name}
        control={control}
        render={({ field, fieldState }) => {
          const hasError = !!fieldState.error;
          const displayError =
            fieldState.error && !hideErrorMessage
              ? (errorMessage ?? fieldState.error.message)
              : undefined;
          const charLength = typeof field.value === "string" ? field.value.length : 0;
          const showCount = showCharCount && maxLength != null;

          return (
            <>
              {(label || showCount) && (
                <Box className="gap-spacing-xs flex flex-row items-start justify-end">
                  {label && (
                    <Label
                      htmlFor={id}
                      required={required}
                      disabled={textAreaProps.disabled}
                      className="min-w-0 flex-1"
                    >
                      {label}
                    </Label>
                  )}
                  {showCount && (
                    <Typography
                      variant="caption-md"
                      color="neutral-foreground-attenuated"
                      className="shrink-0"
                    >
                      {charLength} / {maxLength}
                    </Typography>
                  )}
                </Box>
              )}
              <TextArea
                {...textAreaProps}
                ref={field.ref}
                // Native ignores the Label's htmlFor, so the input carries the label
                accessibilityLabel={textAreaProps.accessibilityLabel ?? label}
                id={id}
                testID={id}
                state={hasError ? "error" : "default"}
                value={field.value ?? ""}
                onChangeText={field.onChange}
                onBlur={field.onBlur}
                maxLength={maxLength}
              />
              <FormFieldMessage error={displayError} helperText={helperText} />
            </>
          );
        }}
      />
    </Box>
  );
};

FormTextArea.displayName = "FormTextArea";
